import {
  Component,
  OnInit,
  AfterViewInit,
  ElementRef,
  ViewChild,
  inject,
  signal,
  DestroyRef,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, Validators, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { CommonModule } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { type Observable } from "rxjs";
import { AuthService } from "../../services/auth.service";
import type { LoginRequest, AuthUser } from "../../interfaces/auth.interface";
import type { AppError } from "../../types/common.types";

@Component({
  selector: "app-login",
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnInit, AfterViewInit {
  @ViewChild("usernameInput") usernameInput?: ElementRef<HTMLInputElement>;

  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  readonly submitting = signal(false);
  readonly errorMessage = signal<string | undefined>(undefined);
  readonly sessionMessage = signal<string | undefined>(undefined);
  readonly loginForm = this.fb.nonNullable.group({
    userName: ["", [Validators.required]],
    password: ["", [Validators.required]],
  });

  // Getters for template bindings (avoid call expressions in template)
  get showUsernameError(): boolean {
    return (
      this.loginForm.controls.userName.invalid &&
      this.loginForm.controls.userName.touched
    );
  }

  get showPasswordError(): boolean {
    return (
      this.loginForm.controls.password.invalid &&
      this.loginForm.controls.password.touched
    );
  }

  get isFormDisabled(): boolean {
    return this.loginForm.invalid || this.submitting();
  }

  get isSubmitting(): boolean {
    return this.submitting();
  }

  get currentErrorMessage(): string | undefined {
    return this.errorMessage();
  }

  get currentSessionMessage(): string | undefined {
    return this.sessionMessage();
  }

  ngOnInit(): void {
    // Redirect authenticated users away from login page
    if (this.authService.isAuthenticated()()) {
      void this.router.navigate(["/"]);
      return;
    }

    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const reason = params.get("reason");
        if (reason === "session-expired") {
          this.sessionMessage.set(
            "Your session expired. Please sign in again.",
          );
        } else if (reason === "unauthorized") {
          this.sessionMessage.set(
            "You are not authorized to access that page.",
          );
        } else if (reason === "unauthenticated") {
          this.sessionMessage.set("Please sign in to continue.");
        }
      });
  }

  ngAfterViewInit(): void {
    // Focus username input after view is initialized
    this.usernameInput?.nativeElement.focus();
  }

  submit(): void {
    if (this.loginForm.invalid || this.submitting()) {
      return;
    }

    this.submitting.set(true);
    this.errorMessage.set(undefined);
    this.sessionMessage.set(undefined);

    const payload: LoginRequest = this.loginForm.getRawValue();

    const loginObservable: Observable<AuthUser> =
      this.authService.login(payload);
    loginObservable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        // Clear password after successful login
        this.loginForm.controls.password.reset();
        void this.router.navigate(["/dashboard"]);
      },
      error: (error: unknown) => {
        this.handleError(error);
        this.submitting.set(false);
        // Preserve username, clear password only
        this.loginForm.controls.password.reset();
      },
      complete: () => this.submitting.set(false),
    });
  }

  private handleError(error: unknown): void {
    if (error instanceof HttpErrorResponse) {
      const status = error.status;
      const message = (error.error as { message?: string } | undefined)
        ?.message;

      // Use API message if provided, otherwise use status-specific defaults
      if (status === 401) {
        this.errorMessage.set(
          message ?? "Invalid username or password. Please try again.",
        );
        return;
      }

      if (status === 423) {
        this.errorMessage.set(
          message ??
            "Too many failed attempts. Please try again in 15 minutes.",
        );
        return;
      }

      if (status === 503) {
        this.errorMessage.set(
          message ??
            "Unable to authenticate at this time. Please try again later.",
        );
        return;
      }

      // Generic error for all other cases
      this.errorMessage.set(message ?? "Unable to sign in. Please try again.");
      return;
    }

    // Check for AppError (from interceptor)
    if (this.isAppError(error)) {
      this.errorMessage.set(error.message);
      return;
    }

    this.errorMessage.set("An unexpected error occurred. Please try again.");
  }

  private isAppError(error: unknown): error is AppError {
    return (
      typeof error === "object" &&
      error !== null &&
      "type" in error &&
      "message" in error
    );
  }
}
